import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { generateARN } from '../data/mockData';
import GSTR1AddRecord from '../components/gstr1/GSTR1AddRecord';
import GSTR1RecordsList from '../components/gstr1/GSTR1RecordsList';
import B2BModuleFlow from '../components/gstr1/B2BModuleFlow';
import GSTSIAAssistant from '../components/gst/GSTSIAAssistant';
import GSTR1ModuleSidebar from '../components/gst/GSTR1ModuleSidebar';
import { 
  ChevronDown, ChevronUp, RefreshCw, Download, 
  ArrowLeft, CheckCircle2, ChevronRight, Info, LogOut
} from 'lucide-react';

// TaxSetu Logo
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_7a5b9feb-7efd-48fe-91ba-ada6a1b3fe62/artifacts/dbzfuj5b_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

// Default company data
const DEFAULT_COMPANY = {
  legalName: 'Demo Company Private Limited',
  tradeName: 'Demo Company',
  gstin: '07AAACG1234A1ZM',
  state: 'Delhi'
};

// GSTR-1 Tiles matching official portal
const gstr1TilesData = [
  { id: '4A,4B,6B,6C', title: '4A, 4B, 6B, 6C - B2B, SEZ, DE Invoices', shortTitle: 'B2B Invoices' },
  { id: '5', title: '5 - B2C (Large) Invoices', shortTitle: 'B2C Large' },
  { id: '6A', title: '6A - Exports Invoices', shortTitle: 'Exports' },
  { id: '7', title: '7 - B2C (Others)', shortTitle: 'B2C Others' },
  { id: '8A,8B,8C,8D', title: '8A, 8B, 8C, 8D - Nil Rated Supplies', shortTitle: 'Nil Rated' },
  { id: '9B-R', title: '9B - Credit / Debit Notes (Registered)', shortTitle: 'CDN Registered' },
  { id: '9B-UR', title: '9B - Credit / Debit Notes (Unregistered)', shortTitle: 'CDN Unregistered' },
  { id: '11A', title: '11A(1), 11A(2) - Tax Liability (Advances Received)', shortTitle: 'Advances Received' },
  { id: '11B', title: '11B(1), 11B(2) - Adjustment of Advances', shortTitle: 'Advances Adjusted' },
  { id: '12', title: '12 - HSN-wise summary of outward supplies', shortTitle: 'HSN Summary' },
  { id: '13', title: '13 - Documents Issued', shortTitle: 'Documents' },
  { id: '14', title: '14 - Supplies made through ECO', shortTitle: 'ECO Supplies' },
  { id: '15', title: '15 - Supplies U/s 9(5)', shortTitle: 'Sec 9(5)' }
];

const GSTR1Dashboard = ({ skipAuth = false, companyData: propCompanyData = null, selectedPeriod: propSelectedPeriod = null, onNavigateToPortal = null, onNavigateToFileReturns = null, onLogout = null }) => {
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(propCompanyData || DEFAULT_COMPANY);
  const [selectedTile, setSelectedTile] = useState(null);
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [showB2BFlow, setShowB2BFlow] = useState(false); // B2B Module Flow (6-step)
  
  // No pre-loaded sample invoices - start fresh
  const [records, setRecords] = useState({});
  
  // Collapsible sections
  const [addRecordExpanded, setAddRecordExpanded] = useState(true);
  const [amendRecordExpanded, setAmendRecordExpanded] = useState(false);
  const [eInvoiceExpanded, setEInvoiceExpanded] = useState(false);
  
  // NIL Return States
  const [fileNilChecked, setFileNilChecked] = useState(false);
  const [showNilReturnModal, setShowNilReturnModal] = useState(false);
  const [nilReturnStep, setNilReturnStep] = useState(1);
  const [nilDeclarationAccepted, setNilDeclarationAccepted] = useState(false);
  const [showNilSuccessModal, setShowNilSuccessModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generatedARN, setGeneratedARN] = useState('');
  
  // Filing Page State
  const [showFilingPage, setShowFilingPage] = useState(false);
  const [filingDeclarationAccepted, setFilingDeclarationAccepted] = useState(false);
  const [selectedSignatory, setSelectedSignatory] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpTimer, setOtpTimer] = useState(30);
  const [otpError, setOtpError] = useState('');
  
  // Module Sidebar State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeModule, setActiveModule] = useState(1); // Default to NIL Return
  
  // Module completion state persisted in localStorage
  const [moduleCompletions, setModuleCompletions] = useState(() => {
    const saved = localStorage.getItem('gstr1_module_completions');
    return saved ? JSON.parse(saved) : { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false };
  });
  
  // Save module completions to localStorage
  useEffect(() => {
    localStorage.setItem('gstr1_module_completions', JSON.stringify(moduleCompletions));
  }, [moduleCompletions]);
  
  // Handle module start/restart (called from sidebar)
  const handleStartModule = (moduleId) => {
    setActiveModule(moduleId);
    // Reset NIL filing state when starting Module 1
    if (moduleId === 1) {
      setFileNilChecked(true);
      setShowFilingPage(false);
      setFilingDeclarationAccepted(false);
      setSelectedSignatory('');
      setOtpValue('');
      setOtpError('');
    } else {
      setFileNilChecked(false);
    }
    setSidebarOpen(false);
  };
  
  // Mark module as completed and save to localStorage immediately
  const markModuleCompleted = (moduleId) => {
    // Get current state from localStorage to avoid stale closure issues
    const currentCompletions = JSON.parse(localStorage.getItem('gstr1_module_completions') || '{}');
    const updatedCompletions = {
      1: false, 2: false, 3: false, 4: false, 5: false, 6: false,
      ...currentCompletions,
      [moduleId]: true
    };
    setModuleCompletions(updatedCompletions);
    // Save to localStorage immediately to ensure persistence before navigation
    localStorage.setItem('gstr1_module_completions', JSON.stringify(updatedCompletions));
  };
  
  // Get Authorised Signatory from localStorage (GST Registration)
  const getAuthorisedSignatory = () => {
    const savedData = localStorage.getItem('practiq_gst_company');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.authorisedSignatory) {
          return parsed.authorisedSignatory;
        }
      } catch (e) {
        console.error('Error loading signatory:', e);
      }
    }
    return 'Mr. ABC';
  };
  
  const authorisedSignatory = getAuthorisedSignatory();

  // Period data - Use selected period if available, otherwise compute from filing period (previous month)
  const currentDate = new Date();
  
  // Compute filing period defaults (returns filed for previous month)
  const computeFilingDefaults = () => {
    const filingDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const filingMonth = filingDate.getMonth();
    const filingYear = filingDate.getFullYear();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    let fyStart;
    if (filingMonth >= 3) fyStart = filingYear;
    else fyStart = filingYear - 1;
    return {
      period: monthNames[filingMonth],
      fy: `${fyStart}-${(fyStart + 1).toString().slice(-2)}`
    };
  };
  const filingDefaults = computeFilingDefaults();
  
  // If selectedPeriod is passed from FileReturnsPage, use it
  const displayMonth = propSelectedPeriod?.period || filingDefaults.period;
  const displayFY = propSelectedPeriod?.financialYear || filingDefaults.fy;
  
  // For backward compatibility, keep these as aliases
  const currentMonth = displayMonth;
  const fyYear = displayFY;
  
  // Calculate due date (11th of next month based on selected period)
  const getMonthIndex = (monthName) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months.indexOf(monthName);
  };
  
  const selectedMonthIndex = getMonthIndex(displayMonth);
  const dueDateMonth = selectedMonthIndex === 11 ? 0 : selectedMonthIndex + 1;
  const dueDateYear = selectedMonthIndex === 11 
    ? parseInt(displayFY.split('-')[0]) + 1 
    : (dueDateMonth < 4 ? parseInt(displayFY.split('-')[0]) + 1 : parseInt(displayFY.split('-')[0]));
  const dueDate = new Date(dueDateYear, dueDateMonth, 11);
  const dueDateStr = dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Load company data from localStorage if not provided via props
  useEffect(() => {
    if (!propCompanyData) {
      const savedData = localStorage.getItem('practiq_gst_company');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setCompanyData({ ...DEFAULT_COMPANY, ...parsed });
        } catch (e) {
          console.error('Error loading company data:', e);
        }
      }
    }
  }, [propCompanyData]);

  const handleTileClick = (tile) => {
    // For B2B tile, open B2B Module Flow dialog (6-step flow)
    if (tile.id === '4A,4B,6B,6C' || tile.shortTitle === 'B2B Invoices') {
      setShowB2BFlow(true);
      return; // Don't set selectedTile for B2B
    }
    // For other tiles, show tile detail view
    setSelectedTile(tile);
  };

  const handleAddRecord = (record) => {
    const tileId = selectedTile.id;
    setRecords(prev => ({
      ...prev,
      [tileId]: [...(prev[tileId] || []), { ...record, id: Date.now() }]
    }));
    setShowAddRecord(false);
  };

  const handleDeleteRecord = (recordId) => {
    const tileId = selectedTile.id;
    setRecords(prev => ({
      ...prev,
      [tileId]: prev[tileId].filter(r => r.id !== recordId)
    }));
  };

  const getRecordCount = (tileId) => {
    return (records[tileId] || []).length;
  };

  const getTotalRecords = () => {
    return Object.values(records).reduce((sum, arr) => sum + arr.length, 0);
  };

  // NIL Return Handlers
  const handleNilCheckChange = (checked) => {
    setFileNilChecked(checked);
    // Don't show modal - just toggle the view
  };

  const handleFileNilStatement = () => {
    // Show filing page instead of modal
    setShowFilingPage(true);
    setFilingDeclarationAccepted(false);
    setSelectedSignatory('');
    setOtpValue('');
    setOtpError('');
  };
  
  const handleFileWithEVC = () => {
    if (filingDeclarationAccepted && selectedSignatory) {
      setShowOtpModal(true);
      setOtpValue('');
      setOtpError('');
      setOtpTimer(30);
    }
  };
  
  const handleVerifyOtp = () => {
    if (otpValue === '123456') {
      const arn = generateARN();
      setGeneratedARN(arn);
      setShowOtpModal(false);
      setShowFilingPage(false);
      setShowNilSuccessModal(true);
      // Mark module as completed immediately when OTP is verified
      markModuleCompleted(1);
    } else {
      setOtpError('Invalid OTP. Please enter 123456');
    }
  };
  
  // OTP Timer Effect
  useEffect(() => {
    let interval;
    if (showOtpModal && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, otpTimer]);
  
  const handleFileWithDSCOrEVC = () => {
    if (filingDeclarationAccepted) {
      const arn = generateARN();
      setGeneratedARN(arn);
      setShowFilingPage(false);
      setShowNilSuccessModal(true);
    }
  };

  const handleNilReturnNext = () => {
    if (nilReturnStep === 1) {
      setNilReturnStep(2);
    } else if (nilReturnStep === 2 && nilDeclarationAccepted) {
      setNilReturnStep(3);
    }
  };

  const handleFileNilReturn = () => {
    const arn = generateARN();
    setGeneratedARN(arn);
    setShowNilReturnModal(false);
    setShowNilSuccessModal(true);
  };

  const handleGenerateSummary = () => {
    const arn = generateARN();
    setGeneratedARN(arn);
    setShowSuccessModal(true);
  };

  // Tile Detail View
  if (selectedTile) {
    return (
      <div className="min-h-screen bg-[#e8f4f8] flex flex-col">
        {/* Header */}
        <header className="bg-[#1e3a5f] text-white">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <img src={TAXSETU_LOGO} alt="TaxSetu" className="h-12" />
              <div className="border-l border-white/30 pl-4">
                <h1 className="text-xl font-serif">Demo Goods and Services Tax</h1>
                <p className="text-sm text-gray-300">Practical Simulation - Not a Government Website - Only for Educational Purpose</p>
              </div>
            </div>
            <div className="text-right text-sm">
              <p className="font-medium">{companyData.legalName}</p>
              <p className="text-gray-300 font-mono text-xs">{companyData.gstin}</p>
            </div>
          </div>
        </header>

        {/* Navigation */}
        <nav className="bg-[#2a5298] py-2 px-6">
          <div className="flex gap-1">
            <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">Dashboard</button>
            <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">Services ▾</button>
            <button className="px-4 py-2 text-yellow-300 text-sm">GST Law</button>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="bg-white border-b py-2 px-6">
          <div className="flex items-center gap-2 text-sm">
            <span 
              className="text-blue-600 cursor-pointer hover:underline" 
              onClick={() => onNavigateToPortal ? onNavigateToPortal() : null}
            >
              Dashboard
            </span>
            <span className="text-gray-400">›</span>
            <span 
              className="text-blue-600 cursor-pointer hover:underline" 
              onClick={() => setSelectedTile(null)}
            >
              GSTR-1
            </span>
            <span className="text-gray-400">›</span>
            <span className="text-gray-600">{selectedTile.shortTitle}</span>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded shadow-sm">
              <div className="border-b px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTile(null)}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <h2 className="font-semibold text-gray-800">{selectedTile.title}</h2>
                    <p className="text-sm text-gray-500">Add invoice details for this section</p>
                  </div>
                </div>
                <Button onClick={() => setShowAddRecord(true)} className="bg-[#1e3a5f] hover:bg-[#2a5298]">
                  + ADD RECORD
                </Button>
              </div>
              <div className="p-6">
                <GSTR1RecordsList 
                  records={records[selectedTile.id] || []} 
                  onDelete={handleDeleteRecord}
                  tileId={selectedTile.id}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Add Record Dialog */}
        <Dialog open={showAddRecord} onOpenChange={setShowAddRecord}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Record - {selectedTile?.title}</DialogTitle>
            </DialogHeader>
            <GSTR1AddRecord 
              tileId={selectedTile?.id}
              onSave={handleAddRecord}
              onCancel={() => setShowAddRecord(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Footer */}
        <footer className="bg-[#2a5298] text-white py-3 px-6">
          <div className="flex items-center justify-between text-xs">
            <span>© 2025-26 Goods and Services Tax Network (Demo)</span>
            <span>Designed & Developed by PractiQ</span>
          </div>
        </footer>
      </div>
    );
  }

  // Filing Page View (when FILE STATEMENT is clicked)
  if (showFilingPage) {
    return (
      <div className="min-h-screen bg-[#e8f4f8] flex flex-col">
        {/* Top Bar */}
        <div className="bg-[#0b1e3f] text-white text-xs py-1.5 px-6 flex justify-between items-center">
          <button 
            onClick={() => setShowFilingPage(false)}
            className="flex items-center gap-1.5 px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-sm transition-colors"
          >
            ← Back
          </button>
          <div className="flex items-center gap-3">
            <span className="text-gray-300">Skip to Main Content</span>
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 bg-yellow-500 rounded text-black text-xs flex items-center justify-center font-bold">0</span>
              <span>A<sup>+</sup></span>
              <span>A<sup>-</sup></span>
            </span>
          </div>
        </div>

        {/* Main Header */}
        <header className="bg-[#1e3a5f] text-white py-4 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={TAXSETU_LOGO} alt="TaxSetu" className="h-14" />
              <div className="border-l border-white/30 pl-4">
                <h1 className="text-2xl font-serif">Demo Goods and Services Tax</h1>
                <p className="text-sm text-gray-300">Practical Simulation - Not a Government Website - Only for Educational Purpose</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-medium flex items-center gap-1">
                <span className="text-gray-300">▲</span>
                {companyData.legalName} ▼
              </p>
              <p className="text-gray-300 font-mono text-sm">{companyData.gstin}</p>
            </div>
          </div>
        </header>

        {/* Navigation */}
        <nav className="bg-[#2a5298]">
          <div className="max-w-7xl mx-auto flex items-center">
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm font-medium">Dashboard</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Services ▾</button>
            <button className="px-5 py-3 text-yellow-300 text-sm">GST Law</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Downloads ▾</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Search Taxpayer ▾</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Help and Taxpayer Facilities</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">e-Invoice</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">News and Updates</button>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="bg-white border-b py-2 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span 
                onClick={() => onNavigateToPortal ? onNavigateToPortal() : navigate('/gst/gstr1')}
                className="text-blue-600 cursor-pointer hover:underline"
              >
                Dashboard
              </span>
              <span className="text-gray-400">›</span>
              <span 
                onClick={() => onNavigateToFileReturns ? onNavigateToFileReturns() : setShowFilingPage(false)}
                className="text-blue-600 cursor-pointer hover:underline"
              >
                Returns
              </span>
              <span className="text-gray-400">›</span>
              <span className="text-gray-600">File</span>
            </div>
            <span className="text-blue-600">🌐 English</span>
          </div>
        </div>

        {/* Company Info Section */}
        <div className="bg-white border-b px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start justify-between">
              <div className="flex gap-16">
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="text-gray-600">GSTIN - </span>
                    <span className="text-gray-900 font-semibold">{companyData.gstin}</span>
                  </p>
                  <p className="text-sm">
                    <span className="text-gray-600">FY - </span>
                    <span className="text-gray-900 font-semibold">{fyYear}</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="text-gray-600">Legal Name - </span>
                    <span className="text-gray-900 font-semibold">{companyData.legalName}</span>
                  </p>
                  <p className="text-sm">
                    <span className="text-gray-600">Return Period - </span>
                    <span className="text-gray-900 font-semibold">{currentMonth}</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="text-gray-600">Return Type - </span>
                    <span className="text-gray-900 font-semibold">GSTR1</span>
                  </p>
                  <p className="text-sm">
                    <span className="text-gray-600">Status - </span>
                    <span className="text-green-600 font-semibold">Ready to File</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="flex items-center justify-end gap-1.5 text-sm">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  <span className="text-gray-600">Indicates Mandatory Fields</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 py-6 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Returns Filing Banner */}
            <div className="bg-gradient-to-r from-[#0d9488] to-[#14b8a6] text-white px-6 py-4 rounded-t flex items-center justify-between mb-0">
              <h2 className="text-lg font-semibold">Returns Filing for GST GSTR1</h2>
              <button className="p-2 bg-blue-600 hover:bg-blue-700 rounded">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {/* Declaration Section */}
            <div className="bg-white rounded-b shadow-sm p-6 mb-4">
              <div className="flex items-start gap-3 mb-6">
                <Checkbox 
                  id="filingDeclaration"
                  checked={filingDeclarationAccepted}
                  onCheckedChange={(checked) => {
                    setFilingDeclarationAccepted(checked);
                    if (!checked) setSelectedSignatory('');
                  }}
                  className="mt-1"
                />
                <label htmlFor="filingDeclaration" className="text-gray-700 cursor-pointer leading-relaxed">
                  I/We hereby solemnly affirm and declare that the information given herein above is true and correct to the best of my knowledge and belief and nothing has been concealed therefrom.
                </label>
              </div>

              {/* Authorised Signatory - Shows after checkbox is checked */}
              {filingDeclarationAccepted && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Authorised Signatory<span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedSignatory}
                    onChange={(e) => setSelectedSignatory(e.target.value)}
                    className="w-80 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value={authorisedSignatory}>{authorisedSignatory}</option>
                  </select>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3">
                <Button 
                  variant="outline" 
                  className="px-8 border-gray-400"
                  onClick={() => setShowFilingPage(false)}
                >
                  BACK
                </Button>
                <Button 
                  className="bg-gray-400 text-white px-6 cursor-not-allowed opacity-60"
                  disabled={true}
                >
                  FILE WITH DSC
                </Button>
                <Button 
                  onClick={handleFileWithEVC}
                  className={`px-6 ${filingDeclarationAccepted && selectedSignatory 
                    ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                    : 'bg-gray-400 text-white cursor-not-allowed opacity-60'}`}
                  disabled={!filingDeclarationAccepted || !selectedSignatory}
                >
                  FILE WITH EVC
                </Button>
              </div>
            </div>

            {/* DSC Usage Steps */}
            <div className="bg-gray-200 rounded shadow-sm p-6">
              <h3 className="text-blue-800 font-semibold mb-4">DSC Usage Steps:</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-gray-500">•</span>
                  <span><strong>Run the emsigner as Administrator.</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-500">•</span>
                  <span><strong>open the portal, fill the appropriate Details go to till Update Register DSC</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-500">•</span>
                  <span><strong>Open a separate tab in same browser and type https://127.0.0.1:1585</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-500">•</span>
                  <span><strong>Click on Advanced</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-500">•</span>
                  <span><strong>Click proceed to 127.0.0.1(unsafe)</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-500">•</span>
                  <span><strong>Come back to GST portal, refresh the page</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-500">•</span>
                  <span><strong>Click on register DSC</strong></span>
                </li>
              </ul>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-[#2a5298] text-white py-3 px-6 mt-auto">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-xs">
            <span>© 2025-26 Goods and Services Tax Network (Demo)</span>
            <span>Site Last Updated on {currentDate.toLocaleDateString('en-GB')}</span>
            <span>Designed & Developed by PractiQ</span>
          </div>
        </footer>

        {/* Success Modal */}
        <Dialog open={showNilSuccessModal} onOpenChange={setShowNilSuccessModal}>
          <DialogContent className="sm:max-w-md">
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <DialogTitle className="text-2xl text-green-700 mb-2">NIL Return Filed Successfully!</DialogTitle>
              <DialogDescription className="text-gray-600 mb-6">
                Your GSTR-1 NIL Return for {currentMonth} {fyYear} has been filed.
              </DialogDescription>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-500 mb-2">Acknowledgment Reference Number (ARN)</p>
                <p className="text-2xl font-mono font-bold text-[#1e3a5f]">{generatedARN}</p>
              </div>

              <div className="bg-amber-50 rounded-lg p-3 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> NIL Return means zero tax liability for this period.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Button onClick={() => { 
                  setShowNilSuccessModal(false); 
                  setShowFilingPage(false); 
                  setFileNilChecked(false);
                  setSidebarOpen(true); // Open sidebar to show next modules
                }} className="w-full bg-[#1e3a5f] hover:bg-[#2a5298]">
                  Practice Next Module
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => { 
                    setShowNilSuccessModal(false); 
                    setShowFilingPage(false); 
                    setFileNilChecked(false);
                    if (onNavigateToPortal) {
                      onNavigateToPortal();
                    } else {
                      navigate('/gst'); 
                    }
                  }} 
                  className="w-full border-gray-300 text-gray-700"
                >
                  Back to GST Portal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* OTP Verification Modal */}
        <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl text-gray-800">Validate One Time Password (OTP)</DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              {/* OTP Sent Message */}
              <div className="bg-green-50 border border-green-200 rounded p-4 mb-6">
                <p className="text-green-700 text-sm">
                  One-Time Password (OTP) has been sent to your registered email ID 
                  <strong> ixxxxxxxxxxxx@gmail.com</strong> and mobile no. <strong>98xxxxxx08</strong>. 
                  OTP is Valid Till <strong>07:01</strong>
                </p>
              </div>

              {/* OTP Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter One Time Password (OTP)
                </label>
                <input
                  type="text"
                  value={otpValue}
                  onChange={(e) => {
                    setOtpValue(e.target.value);
                    setOtpError('');
                  }}
                  placeholder="Enter OTP (123456)"
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={6}
                />
                {otpError && (
                  <p className="text-red-500 text-sm mt-2">{otpError}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <Button 
                  variant="outline" 
                  className="px-6 border-gray-400"
                  onClick={() => setShowOtpModal(false)}
                >
                  CANCEL
                </Button>
                <Button 
                  onClick={handleVerifyOtp}
                  className="bg-[#1e3a5f] hover:bg-[#2a5298] px-6"
                >
                  VERIFY
                </Button>
                <Button 
                  variant="outline"
                  className="px-4 bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
                  onClick={() => setOtpTimer(30)}
                  disabled={otpTimer > 0}
                >
                  RESEND OTP
                </Button>
                <div className="px-4 py-2 border border-gray-300 rounded bg-gray-50 min-w-[60px] text-center">
                  <span className="font-mono font-semibold">{otpTimer}S</span>
                </div>
              </div>

              {/* Help Text */}
              <p className="text-sm text-gray-600 text-center">
                If you do not receive the OTP within 30 seconds, please click "RESEND OTP" button to request same OTP again. 
                Resend request can be made maximum three times.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Dashboard View (Official GST Portal Style)
  return (
    <div className="min-h-screen bg-[#e8f4f8] flex flex-col">
      {/* Module Sidebar */}
      <GSTR1ModuleSidebar 
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        activeModule={activeModule}
        onSelectModule={(moduleId) => {
          setActiveModule(moduleId);
          // If selecting Module 1, enable NIL return flow
          if (moduleId === 1) {
            setFileNilChecked(true);
          } else {
            setFileNilChecked(false);
          }
          // Don't close sidebar on module click - only on Start Module
        }}
        onStartModule={handleStartModule}
        moduleCompletions={moduleCompletions}
      />
      
      {/* Top Bar */}
      <div className="bg-[#0b1e3f] text-white text-xs py-1.5 px-6 flex justify-between items-center">
        <button 
          onClick={() => navigate('/gst')}
          className="flex items-center gap-1.5 px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-sm transition-colors ml-12"
        >
          ← Back
        </button>
        <div className="flex items-center gap-3">
          <span className="text-gray-300">Skip to Main Content</span>
          <span className="flex items-center gap-2">
            <span className="w-5 h-5 bg-yellow-500 rounded text-black text-xs flex items-center justify-center font-bold">0</span>
            <span>A<sup>+</sup></span>
            <span>A<sup>-</sup></span>
          </span>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-[#1e3a5f] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={TAXSETU_LOGO} alt="TaxSetu" className="h-14" />
            <div className="border-l border-white/30 pl-4">
              <h1 className="text-2xl font-serif">Demo Goods and Services Tax</h1>
              <p className="text-sm text-gray-300">Practical Simulation - Not a Government Website - Only for Educational Purpose</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium flex items-center gap-1">
                <span className="text-gray-300">▲</span>
                {companyData.legalName} ▼
              </p>
              <p className="text-gray-300 font-mono text-sm">{companyData.gstin}</p>
            </div>
            {/* Sign Out Button */}
            {onLogout && (
              <button
                onClick={onLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded text-sm transition-colors border border-red-400/50 text-red-200 hover:text-white"
                data-testid="gstr1-logout-btn"
                title="Sign out and change email"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-[#2a5298]">
        <div className="max-w-7xl mx-auto flex items-center">
          <button className="px-5 py-3 text-white hover:bg-white/10 text-sm font-medium">Dashboard</button>
          <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Services ▾</button>
          <button className="px-5 py-3 text-yellow-300 text-sm">GST Law</button>
          <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Downloads ▾</button>
          <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Search Taxpayer ▾</button>
          <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Help and Taxpayer Facilities</button>
          <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">e-Invoice</button>
          <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">News and Updates</button>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-white border-b py-2 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span 
              onClick={() => onNavigateToPortal ? onNavigateToPortal() : null}
              className="text-blue-600 cursor-pointer hover:underline"
              data-testid="breadcrumb-dashboard"
            >
              Dashboard
            </span>
            <span className="text-gray-400">›</span>
            <span 
              onClick={() => onNavigateToFileReturns ? onNavigateToFileReturns() : null}
              className="text-blue-600 cursor-pointer hover:underline"
              data-testid="breadcrumb-returns"
            >
              Returns
            </span>
            <span className="text-gray-400">›</span>
            <span className="text-gray-600">GSTR-1/IFF</span>
          </div>
          <span className="text-blue-600">🌐 English</span>
        </div>
      </div>

      {/* GSTR-1 Title Banner */}
      <div className="bg-gradient-to-r from-[#0d9488] to-[#14b8a6] text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h2 className="text-lg font-semibold">GSTR-1 - Details of outward supplies of goods or services</h2>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-sm font-medium">
              E-INVOICE ADVISORY
            </button>
            <button className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-sm flex items-center gap-1">
              HELP <span className="w-5 h-5 bg-white/30 rounded-full flex items-center justify-center text-xs">0</span>
            </button>
            <button className="p-1.5 bg-white/20 hover:bg-white/30 rounded">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Company Info Section */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start justify-between">
            {/* Left Section */}
            <div className="flex gap-16">
              {/* Column 1 */}
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-gray-600">GSTIN - </span>
                  <span className="text-gray-900 font-semibold">{companyData.gstin}</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-600">FY - </span>
                  <span className="text-gray-900 font-semibold">{fyYear}</span>
                </p>
              </div>
              {/* Column 2 */}
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-gray-600">Legal Name - </span>
                  <span className="text-gray-900 font-semibold">{companyData.legalName}</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-600">Tax Period - </span>
                  <span className="text-gray-900 font-semibold">{currentMonth}</span>
                </p>
              </div>
              {/* Column 3 */}
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-gray-600">Trade Name - </span>
                  <span className="text-gray-900 font-semibold">{companyData.tradeName || companyData.legalName}</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-600">Status - </span>
                  <span className="text-red-600 font-semibold">Not Filed</span>
                </p>
              </div>
            </div>
            {/* Right Section */}
            <div className="text-right space-y-1">
              <p className="flex items-center justify-end gap-1.5 text-sm">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span className="text-gray-600">Indicates Mandatory Fields</span>
              </p>
              <p className="text-sm">
                <span className="text-gray-600">Due Date - </span>
                <span className="text-gray-900 font-semibold">{dueDateStr}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 py-6 px-6">
        <div className="max-w-7xl mx-auto">
          {/* File Nil GSTR-1 Checkbox */}
          <div className="bg-white rounded shadow-sm mb-4 px-6 py-4">
            <div className="flex items-center gap-3">
              <Checkbox 
                id="fileNil"
                checked={fileNilChecked}
                onCheckedChange={handleNilCheckChange}
                className="w-5 h-5"
                data-testid="file-nil-checkbox"
              />
              <label htmlFor="fileNil" className="text-gray-700 cursor-pointer select-none">
                File Nil GSTR-1
              </label>
            </div>
          </div>

          {/* Show FILE STATEMENT when Nil is checked, otherwise show tiles */}
          {fileNilChecked ? (
            /* NIL Return - Note Section (Official GST Portal Style) */
            <div className="bg-white rounded shadow-sm mb-4 overflow-hidden border-l-4 border-green-500">
              <div className="p-6">
                <p className="text-green-700 font-semibold italic mb-4">
                  Note: NIL Form GSTR-1 can be filed by you if you have:
                </p>
                <div className="text-green-700 space-y-2 ml-4">
                  <p>
                    <span className="font-semibold">a.</span> No Outward Supplies (including supplies on which tax is to be charged on reverse charge basis, zero rated supplies and deemed exports) during the month or quarter for which the form is being filed for
                  </p>
                  <p>
                    <span className="font-semibold">b.</span> No Amendments to be made to any of the supplies declared in an earlier form
                  </p>
                  <p>
                    <span className="font-semibold">c.</span> No Credit or Debit Notes to be declared / amended
                  </p>
                  <p>
                    <span className="font-semibold">d.</span> No details of advances received for services is to be declared or adjusted
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Regular View - ADD RECORD DETAILS Section */
            <>
              <div className="bg-white rounded shadow-sm mb-4 overflow-hidden">
                <button 
                  onClick={() => setAddRecordExpanded(!addRecordExpanded)}
                  className="w-full bg-[#1e3a5f] text-white px-6 py-3 flex items-center justify-between"
                >
                  <span className="font-semibold">ADD RECORD DETAILS</span>
                  {addRecordExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                
                {addRecordExpanded && (
                  <div className="p-6">
                    <div className="grid grid-cols-4 gap-4">
                      {gstr1TilesData.map((tile) => {
                        const count = getRecordCount(tile.id);
                        return (
                          <div 
                            key={tile.id}
                            onClick={() => handleTileClick(tile)}
                            className="border rounded overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                            data-testid={`tile-${tile.id}`}
                          >
                            <div className="bg-[#1e3a5f] text-white px-3 py-3 text-center">
                              <p className="text-sm font-medium leading-tight">{tile.title}</p>
                            </div>
                            <div className="bg-white px-3 py-4 text-center">
                              <p className="flex items-center justify-center gap-1 text-green-600 font-semibold">
                                <CheckCircle2 className="w-5 h-5" />
                                <span>{count}</span>
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* AMEND RECORD DETAILS Section */}
              <div className="bg-white rounded shadow-sm mb-4 overflow-hidden">
                <button 
                  onClick={() => setAmendRecordExpanded(!amendRecordExpanded)}
                  className="w-full bg-[#1e3a5f] text-white px-6 py-3 flex items-center justify-between"
                >
                  <span className="font-semibold">AMEND RECORD DETAILS</span>
                  {amendRecordExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                
                {amendRecordExpanded && (
                  <div className="p-6">
                    <p className="text-gray-500 text-center">No amendments to show</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* E-Invoice Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                The taxpayers for whom e-invoicing is not applicable may ignore the sections/options related to e-invoice download. 
                The downloaded file would be blank in case taxpayer is not e-invoicing or when e-invoices reported to IRP are yet to be processed by GST system
              </p>
            </div>
          </div>

          {/* E-INVOICE DOWNLOAD HISTORY Section */}
          <div className="bg-white rounded shadow-sm mb-6 overflow-hidden">
            <button 
              onClick={() => setEInvoiceExpanded(!eInvoiceExpanded)}
              className="w-full bg-[#1e3a5f] text-white px-6 py-3 flex items-center justify-between"
            >
              <span className="font-semibold">E-INVOICE DOWNLOAD HISTORY</span>
              {eInvoiceExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {eInvoiceExpanded && (
              <div className="p-6">
                <p className="text-gray-500 text-center">No e-invoice download history</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4">
            <Button 
              variant="outline" 
              className="px-8 border-gray-400"
              onClick={() => onNavigateToFileReturns ? onNavigateToFileReturns() : navigate('/gst')}
            >
              BACK
            </Button>
            <Button className="bg-[#1e3a5f] hover:bg-[#2a5298] px-6">
              <Download className="w-4 h-4 mr-2" />
              DOWNLOAD DETAILS FROM E-INVOICES (EXCEL)
            </Button>
            <Button 
              variant="outline" 
              className="px-8 bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
            >
              RESET
            </Button>
            {fileNilChecked ? (
              <Button 
                onClick={handleFileNilStatement}
                className="bg-[#1e3a5f] hover:bg-[#2a5298] px-8"
              >
                FILE STATEMENT
              </Button>
            ) : (
              <Button 
                onClick={handleGenerateSummary}
                className="bg-[#1e3a5f] hover:bg-[#2a5298] px-8"
                disabled={getTotalRecords() === 0}
              >
                GENERATE SUMMARY
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2a5298] text-white py-3 px-6 mt-auto">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs">
          <span>© 2025-26 Goods and Services Tax Network (Demo)</span>
          <span>Site Last Updated on {currentDate.toLocaleDateString('en-GB')}</span>
          <span>Designed & Developed by PractiQ</span>
        </div>
      </footer>

      {/* NIL Return Modal - Declaration and File Steps Only */}
      <Dialog open={showNilReturnModal} onOpenChange={(open) => {
        setShowNilReturnModal(open);
      }}>
        <DialogContent className="sm:max-w-2xl">
          {nilReturnStep === 2 && (
            <div className="py-4">
              <DialogHeader className="mb-6">
                <DialogTitle className="text-xl text-[#1e3a5f]">Declaration for NIL GSTR-1</DialogTitle>
                <DialogDescription>For return period: {currentMonth} {fyYear}</DialogDescription>
              </DialogHeader>

              <div className="bg-gray-50 border rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-gray-800 mb-4">Declaration</h4>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  I hereby declare that the information given in this form is true and correct. I confirm that during the tax period <strong>{currentMonth} {fyYear}</strong>, there were:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 mb-4">
                  <li>No outward supplies of goods or services</li>
                  <li>No zero-rated supplies</li>
                  <li>No supplies on which tax is to be paid on reverse charge basis</li>
                  <li>No credit/debit notes to be reported</li>
                </ul>
              </div>

              <div className="flex items-start gap-3 mb-6 p-4 border rounded-lg">
                <Checkbox 
                  id="nilDeclaration"
                  checked={nilDeclarationAccepted}
                  onCheckedChange={setNilDeclarationAccepted}
                  className="mt-0.5"
                />
                <label htmlFor="nilDeclaration" className="text-sm text-gray-700 cursor-pointer">
                  I have read and understood the above declaration and hereby confirm that the details furnished in GSTR-1 NIL return are true and correct.
                </label>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowNilReturnModal(false)}>Cancel</Button>
                <Button onClick={() => setNilReturnStep(3)} className="bg-[#1e3a5f] hover:bg-[#2a5298]" disabled={!nilDeclarationAccepted}>
                  Continue <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {nilReturnStep === 3 && (
            <div className="py-4">
              <DialogHeader className="mb-6">
                <DialogTitle className="text-xl text-[#1e3a5f]">File NIL Return with EVC/DSC</DialogTitle>
                <DialogDescription>Choose authentication method</DialogDescription>
              </DialogHeader>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">NIL Return Summary</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600">GSTIN:</span> <span className="font-mono font-semibold ml-2">{companyData.gstin}</span></div>
                  <div><span className="text-gray-600">Period:</span> <span className="font-semibold ml-2">{currentMonth} {fyYear}</span></div>
                  <div><span className="text-gray-600">Total Liability:</span> <span className="font-semibold text-green-700 ml-2">₹ 0.00</span></div>
                  <div><span className="text-gray-600">Return Type:</span> <span className="font-semibold ml-2">NIL Return</span></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <button onClick={handleFileNilReturn} className="p-4 border-2 border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="font-semibold text-gray-800">File with EVC</span>
                  </div>
                  <p className="text-xs text-gray-500">Electronic Verification Code via Aadhaar OTP</p>
                </button>
                <button onClick={handleFileNilReturn} className="p-4 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="font-semibold text-gray-800">File with DSC</span>
                  </div>
                  <p className="text-xs text-gray-500">Digital Signature Certificate</p>
                </button>
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setNilReturnStep(2)}>Back</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NIL Return Success Modal */}
      <Dialog open={showNilSuccessModal} onOpenChange={setShowNilSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <DialogTitle className="text-2xl text-green-700 mb-2">NIL Return Filed Successfully!</DialogTitle>
            <DialogDescription className="text-gray-600 mb-6">
              Your GSTR-1 NIL Return for {currentMonth} {fyYear} has been filed.
            </DialogDescription>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-500 mb-2">Acknowledgment Reference Number (ARN)</p>
              <p className="text-2xl font-mono font-bold text-[#1e3a5f]">{generatedARN}</p>
            </div>

            <div className="bg-amber-50 rounded-lg p-3 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> NIL Return means zero tax liability for this period.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button onClick={() => { 
                setShowNilSuccessModal(false); 
                setSidebarOpen(true);
              }} className="w-full bg-[#1e3a5f] hover:bg-[#2a5298]">
                Practice Next Module
              </Button>
              <Button 
                variant="outline"
                onClick={() => { 
                  setShowNilSuccessModal(false);
                  if (onNavigateToPortal) {
                    onNavigateToPortal();
                  } else {
                    navigate('/gst'); 
                  }
                }} 
                className="w-full border-gray-300 text-gray-700"
              >
                Back to GST Portal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Regular Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <DialogTitle className="text-2xl text-green-700 mb-2">Return Filed Successfully!</DialogTitle>
            <DialogDescription className="text-gray-600 mb-6">Your GSTR-1 has been filed successfully.</DialogDescription>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-2">Acknowledgment Reference Number (ARN)</p>
              <p className="text-2xl font-mono font-bold text-[#1e3a5f]">{generatedARN}</p>
            </div>

            <div className="flex flex-col gap-3">
              <Button onClick={() => { 
                setShowSuccessModal(false); 
                setSidebarOpen(true);
              }} className="w-full bg-[#1e3a5f] hover:bg-[#2a5298]">
                Practice Next Module
              </Button>
              <Button 
                variant="outline"
                onClick={() => { 
                  setShowSuccessModal(false);
                  if (onNavigateToPortal) {
                    onNavigateToPortal();
                  } else {
                    navigate('/gst'); 
                  }
                }} 
                className="w-full border-gray-300 text-gray-700"
              >
                Back to GST Portal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* B2B Module Flow Dialog - NEW 6-step flow */}
      <Dialog open={showB2BFlow} onOpenChange={setShowB2BFlow}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto p-0">
          <B2BModuleFlow 
            records={records['4A,4B,6B,6C'] || []}
            companyData={companyData}
            onClose={() => setShowB2BFlow(false)}
            onUpdateRecords={(newRecords) => {
              setRecords(prev => ({
                ...prev,
                '4A,4B,6B,6C': newRecords
              }));
            }}
            onComplete={() => {
              markModuleCompleted(2);
              setShowB2BFlow(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* SIA Assistant */}
      <GSTSIAAssistant formType="GSTR-1" />
    </div>
  );
};

export default GSTR1Dashboard;
